import { zodResolver } from '@hookform/resolvers/zod';

import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect } from 'react';
import { Controller,useForm } from 'react-hook-form';

import { ActionItemStatus, generateTempTagEkhoFormSchema } from '@ekhodealer/ekho-common';
import { Button, SkeletonRect,Stack, Text, TextInput, toast } from '@ekhodealer/ekho-common/rev';

import type { ActionItem, GenerateTempTagEkhoFormData } from '@ekhodealer/ekho-common';

import { InfoLineItem } from '@common/components/InfoLineItem';
import { formatTimestampToDate } from '@common/lib/time';

import { useGenerateTempTag } from '../../../api/progress/useGenerateTempTag';
import { useGetGenerateTempTag } from '../../../api/progress/useGetGenerateTempTag';
import { navigateBackAtom, orderDetailsDrawerAtom } from '../../../atoms/orderDetailsDrawerAtoms';

import { downloadPdf } from './utils/downloadPdf';

interface GenerateTempTagEkhoDetailProps {
	actionItem: ActionItem;
}

/**
 * GenerateTempTagEkhoDetail component for generating a temp tag packet via Ekho internal workflow.
 * Displays inputs for temp tag number and NAIC code, shows buyer info, and generates a PDF packet on submit.
 */
export const GenerateTempTagEkhoDetail = ({ actionItem }: GenerateTempTagEkhoDetailProps) => {
	const { selectedRow } = useAtomValue(orderDetailsDrawerAtom);
	const orderId = selectedRow?.purchasedVehicleId;
	const navigateBack = useSetAtom(navigateBackAtom);

	const generateTempTagMutation = useGenerateTempTag(orderId);
	const { data: tempTagData, isLoading: isLoadingTempTagData } = useGetGenerateTempTag(orderId);

	const {
		control,
		handleSubmit,
		formState: { errors, isSubmitting },
		reset,
	} = useForm<GenerateTempTagEkhoFormData>({
		resolver: zodResolver(generateTempTagEkhoFormSchema),
		defaultValues: {
			tempTagNumber: '',
			naicCode: '',
		},
	});

	// Pre-populate form with existing temp tag data
	useEffect(() => {
		if (tempTagData) {
			reset({
				tempTagNumber: tempTagData.tempTagNumber || '',
				naicCode: tempTagData.naicCode || '',
			});
		}
	}, [tempTagData, reset]);

	const submitHandler = handleSubmit(async (data: GenerateTempTagEkhoFormData) => {
		try {
			const result = await generateTempTagMutation.mutateAsync({
				tempTagNumber: (data.tempTagNumber ?? '').trim(),
				naicCode: (data.naicCode ?? '').trim(),
			});

			// Download the PDF if returned
			if (result.pdfBase64) {
				const filename = `temp-tag-packet-${orderId}-${data.tempTagNumber}.pdf`;
				downloadPdf(result.pdfBase64, filename);
			}

			toast({
				message: 'Temp tag packet generated successfully',
				variant: 'success',
			});
			// Navigate back to progress view on success
			navigateBack();
			// Reset form on success
			reset();
		} catch (err) {
			const errorMessage =
				err instanceof Error ? err.message : 'Failed to generate temp tag packet';
			toast({
				message: errorMessage,
				variant: 'error',
			});
		}
	});

	const isFormDisabled =
		isSubmitting ||
		generateTempTagMutation.isPending ||
		actionItem.status === ActionItemStatus.COMPLETE;

	return (
		<Stack itemsSpacing='24'>
			<Stack itemsSpacing='8'>
				<Text size='body' weight='semibold'>
					{actionItem.config.title}
				</Text>
				<Text size='bodySmall' color='secondary'>
					First, manually fill out a physical temp tag form. Then enter the temp tag number and NAIC code below to generate a shipping packet (cover letter, shipping label, and DMV form) for mailing the physical temp tag to the buyer.
				</Text>
			</Stack>

			{/* Display buyer, vehicle, and address info */}
			{isLoadingTempTagData ? (
				<Stack itemsSpacing='8'>
					<SkeletonRect width='60%' height={20} />
					<SkeletonRect width='80%' height={20} />
					<SkeletonRect width='70%' height={20} />
				</Stack>
			) : (
				tempTagData && (
					<Stack itemsSpacing='12'>
						{/* Buyer Info */}
						<InfoLineItem title='First name' value={tempTagData.firstName} />
						<InfoLineItem title='Middle name' value={tempTagData.middleName} />
						<InfoLineItem title='Last name' value={tempTagData.lastName} />
						<InfoLineItem title='DL number' value={tempTagData.dlNumber} />
						<InfoLineItem title='DL state' value={tempTagData.dlState} />

						{/* Address Info */}
						<InfoLineItem title='Address' value={tempTagData.address} />
						<InfoLineItem title='State' value={tempTagData.state} />

						{/* Vehicle Info */}
						<InfoLineItem title='Make' value={tempTagData.make} />
						<InfoLineItem title='Model' value={tempTagData.model} />
						<InfoLineItem title='VIN' value={tempTagData.vin} isCopyable />
						<InfoLineItem title='Year' value={tempTagData.year?.toString()} />
						<InfoLineItem title='Body type' value={tempTagData.bodyType} />
						<InfoLineItem title='Color' value={tempTagData.color} />

						{/* Temp Tag Info */}
						<InfoLineItem
							title='Temp tag expiration'
							value={
								tempTagData.tempTagExpirationDate
									? formatTimestampToDate(new Date(tempTagData.tempTagExpirationDate).getTime()) ?? '—'
									: undefined
							}
						/>

						{/* Insurance Info */}
						<InfoLineItem
							title='Insurance carrier'
							value={tempTagData.insuranceCarrierName}
						/>
					</Stack>
				)
			)}

			<Stack itemsSpacing='16'>
				{isLoadingTempTagData ? (
					<>
						<SkeletonRect width='100%' height={48} />
						<SkeletonRect width='100%' height={48} />
					</>
				) : (
					<>
						<Controller
							name='tempTagNumber'
							control={control}
							render={({ field: { onChange, onBlur, value } }) => (
								<Stack itemsSpacing='4'>
									<Text size='footnote'>Temp Tag Number</Text>
									<TextInput
										placeholder='Enter temp tag number'
										value={value}
										onChange={onChange}
										onBlur={onBlur}
										size='large'
										hasError={errors.tempTagNumber?.message}
										isDisabled={isFormDisabled}
									/>
									{errors.tempTagNumber && (
										<Text size='footnote' color='error'>
											{errors.tempTagNumber.message}
										</Text>
									)}
								</Stack>
							)}
						/>

						<Controller
							name='naicCode'
							control={control}
							render={({ field: { onChange, onBlur, value } }) => (
								<Stack itemsSpacing='4'>
									<Text size='footnote'>NAIC Code</Text>
									<TextInput
										placeholder='Enter insurance provider NAIC code'
										value={value}
										onChange={onChange}
										onBlur={onBlur}
										size='large'
										hasError={errors.naicCode?.message}
										isDisabled={isFormDisabled}
									/>
									{errors.naicCode && (
										<Text size='footnote' color='error'>
											{errors.naicCode.message}
										</Text>
									)}
								</Stack>
							)}
						/>
					</>
				)}

				{actionItem.status !== ActionItemStatus.COMPLETE && (
					<Button
						size='large'
						isFullWidth
						onPress={() => {
							submitHandler();
						}}
						isLoading={isSubmitting || generateTempTagMutation.isPending}
						isDisabled={isLoadingTempTagData}>
						{'Generate Temp Tag Packet'}
					</Button>
				)}
			</Stack>
		</Stack>
	);
};
